import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import GenericPopUpModal from 'components/VirtualPortal/components/PopUpModal'
import EventEvalModal from 'components/VirtualPortal/components/EventEvalModal'
import InvalidTokenScreen from 'components/VirtualPortal/components/InvalidTokenScreen'
import LoadingScreen from 'components/VirtualPortal/components/LoadingScreen'
import startChatProgram from 'components/VirtualPortal/helpers/chatProgram.js'
import SessionRow from './SessionRow'
import NoAgenda from './NoAgenda.jsx'
import 'components/VirtualPortal/css/sessions.scss'
import 'components/VirtualPortal/css/buttonSlider.css'
import 'moment-timezone'
import _ from 'lodash'

const Sessions = ({
  peerToPeerWizardIsAvailable,
  flashMessage,
  virtualPortalPreRegistrationClosed,
}) => {
  const params = useParams()
  const [state, setState] = useState({
    agendaFilters: { mySchedule: false },
    genericModalIsOpen: false,
    showEvalModal: false,
    hasValidToken: true,
    isRefreshing: true,
    fetchingErrorMessage: '',
    isEventDay: false,
    surveyUserRegistrationStatusKey: '',
    sessions: [],
  })
  const buttonStatus = state.agendaFilters?.mySchedule

  const fetchSessions = () => {
    const desktopOrMobile = () => {
      const windowSize = window.innerWidth
      const desktopOrMobile = windowSize >= 767 ? 'desktop' : 'mobile'
      return desktopOrMobile
    }

    window.addEventListener('resize', desktopOrMobile)

    window.api
      .get('/virtual_portal/sessions_from_token')
      .then((r) => {
        const data = {
          ...state,
          isRefreshing: false,
          hasValidToken: true,
          sessions: r.data.sessions,
          eventName: r.data.meta.event_info.event_name,
          eventHasPeerToPeer: r.data.meta.event_info.event_has_peer_to_peer,
          eventIsColo: r.data.meta.event_info.is_colo,
          eventBrand: r.data.meta.event_info.brand,
          userHasPeerToPeer: r.data.meta.current_user.user_has_peer_to_peer,
          eventBeginDate: r.data.meta.event_info.event_begin_date,
          eventBeginTime: r.data.meta.event_info.event_ics_begin_datetime,
          eventEndTime: r.data.meta.event_info.event_ics_end_datetime,
          eventTimeZone: r.data.meta.event_info.event_timezone,
          userFullName: r.data.meta.current_user.full_name,
          userPhoto: r.data.meta.current_user.photo,
          userTitle: r.data.meta.current_user.title,
          userOrganizationName: r.data.meta.current_user.organization_name,
          userDetails: {
            status: r.data.meta.current_user.status,
            crm_contact_id: r.data.meta.current_user.crm_contact_id,
            sponsor_id: r.data.meta.current_user.sponsor_id,
            p2p_opt_in: r.data.meta.current_user.user_has_peer_to_peer,
            can_take_session_survey:
              r.data.meta.current_user.can_take_session_survey,
          },
          eventEvalUrl: r.data.meta.event_info.event_evaluation_url,
          hasThankYou: [
            'registration_status_approved',
            'registration_status_approved_internal',
          ].includes(
            r.data.meta.event_info.survey_user_registration_status_key
          ),
          upcomingEvents: r.data.meta.event_info.upcoming_events,
          oneOnOneWizardIsComplete:
            r.data.meta.current_user.one_on_one_wizard_is_complete,
          lpsIsComplete: r.data.meta.current_user.lps_is_complete,
          lpsSurveyLink: r.data.meta.current_user.lps_survey_link,
          eventAgendaStatusKey: r.data.meta.event_info.event_agenda_status_key,
          eventMarketingUrl: r.data.meta.event_info.event_marketing_url,
          surveyUserRegistrationStatusKey:
            r.data.meta.event_info.survey_user_registration_status_key,
          desktopOrMobile: desktopOrMobile(),
        }
        let isEventDay = false
        if (r.data.sessions && r.data.sessions[0]) {
          const session = r.data.sessions[0]
          const currentTime = parseInt(session.server_time, 10)
          // 2 hours before first session
          const startTime = parseInt(session.epoch_start_time, 10) - 7200
          isEventDay = currentTime > startTime
        }
        setState({ ...data, isEventDay })
        let dataLayer = (window.dataLayer = window.dataLayer || [])
        dataLayer.push({
          event_id: r.data.meta.event_info.id,
          event_name: r.data.meta.event_info.event_name,
          event_type: r.data.meta.event_info.event_type,
          brand: r.data.meta.event_info.brand,
          region: r.data.meta.event_info.region,
          crm_contact_id: r.data.meta.current_user.crm_contact_id,
        })
        window.Rollbar.configure({
          payload: {
            person: {
              id: r.data.meta.current_user.crm_contact_id,
              username: r.data.meta.current_user.full_name,
            },
          },
        })
        window.__lc.params = [
          { name: 'Name', value: r.data.meta.current_user.full_name },
          { name: 'CrmID', value: r.data.meta.current_user.crm_contact_id },
          { name: 'EventName', value: r.data.meta.event_info.event_name },
          { name: 'EventId', value: r.data.meta.event_info.id },
        ]
        startChatProgram()
        if (window.LiveChatWidget) {
          window.LiveChatWidget.call(
            'set_customer_name',
            r.data.meta.current_user.full_name +
              ' (' +
              r.data.meta.event_info.event_name +
              ') '
          )
        }
      })
      .catch((e) => {
        setState({
          hasValidToken: false,
          fetchingErrorMessage: e.message,
        })
      })
  }

  useEffect(() => {
    fetchSessions()
  }, [])

  useEffect(() => {
    let timeoutTime = 0

    if (state.sessions?.length) {
      const session = state.sessions[0]
      const currentTime = parseInt(session.server_time)
      const startTime = parseInt(session.epoch_start_time) - 7200
      timeoutTime = Math.abs((startTime - currentTime) * 1000)
    }

    // Timeout >= 2^31 not supported by setTimeout
    if(timeoutTime >= 2**31) { 
      timeoutTime = 2**31 - 1
    }

    if (!state.isEventDay && timeoutTime > 0) {
      const timeout = setTimeout(() => {
        fetchSessions()
      }, timeoutTime)

      return () => {
        // clears timeout before running the new effect
        clearTimeout(timeout)
      }
    }
  }, [state])

  useEffect(() => {
    if (!state.sessions?.length) {
      setState({ ...state, showEvalModal: false, isEventOver: false })
      return
    } else {
      const currentTime = Math.floor(new Date().getTime() / 1000)
      let reversedSessions = state.sessions.slice().reverse()
      const lastSessionEnd = parseInt(reversedSessions[0].epoch_end_time)
      if (currentTime >= lastSessionEnd) {
        setState({ ...state, showEvalModal: true, isEventOver: true })
      } else {
        // if lastSessionEnd - current time is greater than 1 day (in seconds)
        if (lastSessionEnd - currentTime >= 86400) {
          // Reload the page in 24 hours
          if (!window.reloadTimout) {
            window.reloadTimout = setTimeout(
              () => window.location.reload(),
              86400000
            )
          }
        } else {
          window.endTimeout = setTimeout(
            () =>
              setState({ ...state, showEvalModal: true, isEventOver: true }),
            (lastSessionEnd - currentTime) * 1000,
            this
          )
        }
      }
    }
  }, [state.sessions])

  const displayLpsAlert = useCallback(() => {
    if (
      state.surveyUserRegistrationStatusKey ===
        'registration_status_approved' &&
      state.lpsIsComplete
    ) {
      return false
    } else if (state.eventBrand === 'CSO') {
      return false
    } else if (state.eventIsColo === true) {
      return false
      // if event is in past year, do not show LPS
    } else if (
      new Date(state.eventBeginDate).getFullYear() < new Date().getFullYear()
    ) {
      return false
    } else {
      return true
    }
  }, [state])

  const filterSessions = useCallback(() => {
    let sessions = state.sessions
    if (state.agendaFilters.mySchedule) {
      let inScheduleStatuses = ['un_register']
      sessions.forEach((session) => {
        if (!session.itinerary) {
          if (session.session_type_key == 'session_type_one_to_one_meeting') {
            session.is_filtered = false
          } else {
            session.is_filtered = !inScheduleStatuses.includes(
              session.registration_status
            )
          }
        }
      })
    } else {
      sessions.forEach((session) => {
        session.is_filtered = false
      })
    }
    return sessions
  }, [state])

  const updateSessions = useCallback(
    (agendaFilters) => {
      let sessions = filterSessions(agendaFilters)
      setState({ ...state, sessions, agendaFilters })
    },
    [filterSessions, state]
  )

  const toggleFilter = useCallback(
    (filterKey, secondaryKey) => {
      let { agendaFilters } = state
      if (secondaryKey) {
        agendaFilters[filterKey][secondaryKey] =
          !agendaFilters[filterKey][secondaryKey]
      } else {
        agendaFilters[filterKey] = !agendaFilters[filterKey]
      }
      updateSessions(agendaFilters)
    },
    [state, updateSessions]
  )

  const renderSwitch = useCallback(() => {
    switch (window.currentEvent.id) {
    case 4557:
      return (
        <table>
          <tr>
            <td>View: </td>
            <td>
              <a href="#day_1">
                  Day 1: October 7, 2021 | 8:00am - 3:30pm PDT
              </a>
            </td>
          </tr>
          <tr>
            <td></td>
            <td>
              <a href="#day_2">
                  Day 2: October 8, 2021 | 8:00am - 11:30am PDT
              </a>
            </td>
          </tr>
        </table>
      )
    case 4558:
      return (
        <table>
          <tr>
            <td>View: </td>
            <td>
              <a href="#day_1">
                  Day 1: October 12, 2021 | 8:00am - 4:00pm PDT
              </a>
            </td>
          </tr>
          <tr>
            <td></td>
            <td>
              <a href="#day_2">
                  Day 2: October 13, 2021 | 8:00am - 11:30am PDT
              </a>
            </td>
          </tr>
        </table>
      )
    default:
      return ''
    }
  }, [])

  const submitOptIn = useCallback(() => {
    window.api.get('/virtual_portal/opt_in_p2p').then(() => {
      setState({
        ...state,
        genericModalIsOpen: false,
        isRefreshing: true,
      })
      let dataLayer = (window.dataLayer = window.dataLayer || [])
      dataLayer.push({
        event: 'generic-ga-event',
        eventCategory: 'Virtual Portal',
        eventAction: 'P2P Opt-In Completed from Portal',
      })
      window.location.href = '/' + params.token + '/profile/wizard'
    })
  }, [params.token])

  const p2PModal = useCallback(() => {
    const genericModalContent = `<h4>Opt in to Participate in Peer-to-Peer Meetings</h4>
                  <p>Peer-to-Peer Meetings are 25-minute, one-on-one meetings with peers in your community. I agree to participate in Peer-to-Peer Meetings as part of this event.</p>`
    const genericModalCustomCancelButton = (
      <div>
        <button
          onClick={() => submitOptIn()}
          className="btn btn-danger close-modal"
        >
          CONTINUE
        </button>
      </div>
    )
    setState({
      ...state,
      genericModalIsOpen: true,
      genericModalContent,
      genericModalCustomCancelButton,
    })
  }, [state, submitOptIn])

  const getSessions = useCallback(
    (sessions) => {
      let needsRefresh =
        sessions.length === 1 &&
        sessions[0].registration_status === 'add_to_agenda' &&
        !sessions[0].itinerary
      let returnedSessions = []
      sessions.forEach((session) => {
        if (!session.is_hidden && !session.is_filtered) {
          let sessionRow = session.itinerary ? (
            <div key={session.id} className="session itinerary">
              <p className="session-type">{session.session_type}</p>
            </div>
          ) : (
            <SessionRow
              key={session.id}
              token={params.token}
              session={session}
              flashMessage={flashMessage}
              isEventDay={state.isEventDay}
              needsRefresh={needsRefresh}
              userDetails={state.userDetails}
              p2PModal={p2PModal}
              oneOnOneWizardIsComplete={state.oneOnOneWizardIsComplete}
              virtualPortalPreRegistrationClosed={
                virtualPortalPreRegistrationClosed
              }
            />
          )
          returnedSessions.push(sessionRow)
        }
      })
      const noResultsMessage = (
        <div className="no-results-message">Loading.</div>
      )
      return returnedSessions.length ? returnedSessions : noResultsMessage
    },
    [virtualPortalPreRegistrationClosed, state, params, p2PModal, flashMessage]
  )

  const setImageUrl = useCallback(
    (sessions) => {
      let i = 1
      sessions = _.each(sessions, (session) => {
        if (session.itinerary || session.is_hidden) {
          return
        } else {
          session.imageUrl = `https://cfassets.evanta.com/app-assets/Portal-VBR${i}-${state.desktopOrMobile}.jpg`
          i = i === 12 && sessions.length > 12 ? 1 : (i += 1)
        }
      })
      return sessions
    },
    [state]
  )

  const getTimeBlocks = useCallback(() => {
    let lastDate = ''
    let dayNumber = 1
    let sessions = []
    let timeBlocks = _.uniq(
      _.map(state.sessions, function (session) {
        return session.formatted_display_date
      })
    )
    timeBlocks.forEach((timeBlock) => {
      let link = ''
      let showHide = 'hide'
      if (
        [4557, 4558].includes(window.currentEvent.id) &&
        lastDate != timeBlock.substr(0, timeBlock.indexOf('|') - 1)
      ) {
        lastDate = timeBlock.substr(0, timeBlock.indexOf('|') - 1)
        link = 'day_' + dayNumber++
        showHide = ''
      }
      let updatedSessions = setImageUrl(state.sessions)
      let timeBlockSessions = _.filter(
        updatedSessions,
        (session) =>
          session.formatted_display_date === timeBlock &&
          !session.is_hidden &&
          !session.is_filtered
      )
      let firstP2p = _.find(
        updatedSessions,
        (session) =>
          session.session_type_key == 'session_type_one_to_one_meeting'
      )
      if (timeBlockSessions.length) {
        sessions.push(
          <section key={timeBlock}>
            <hr id={link} className={'day-break ' + showHide}></hr>
            <div className="time-block-section container" key={timeBlock}>
              <p
                id={
                  firstP2p && firstP2p.id === timeBlockSessions[0].id
                    ? 'p2p'
                    : ''
                }
                className="formatted-time"
              >
                {timeBlock}
              </p>
              {getSessions(timeBlockSessions)}
            </div>
          </section>
        )
      }
    })
    return sessions.length ? <div>{sessions}</div> : ''
  }, [state])

  const handleCloseEventEvalModal = useCallback(() => {
    setState({ ...state, showEvalModal: false })
  }, [state])

  const handleCloseGenericModal = useCallback(() => {
    setState({ ...state, genericModalIsOpen: false })
  }, [state])

  const handleTokenInputChange = useCallback(
    (event) => {
      setState({ ...state, tokenInputValue: event.target.value })
    },
    [state]
  )

  const handleTokenInputSubmit = useCallback(
    () =>
      (window.location = window.location.origin + '/' + state.tokenInputValue),
    [state]
  )

  if (!state.hasValidToken) {
    return (
      <InvalidTokenScreen
        handleTokenInputChange={() => handleTokenInputChange()}
        handleTokenSubmit={() => handleTokenInputSubmit}
        token={params.token}
        tokenInputValue={state.tokenInputValue}
        fetchingErrorMessage={state.fetchingErrorMessage}
      />
    )
  }

  if (state.isRefreshing) {
    return <LoadingScreen />
  }

  return (
    <div>
      <div className="container alert-section">
        {displayLpsAlert() ? (
          <a
            href={state.lpsSurveyLink}
            target="_blank"
            rel="noreferrer"
            className="header-alert"
          >
            <strong>ALERT</strong> Complete Leadership Perspective Survey{' '}
            <i className="fa fa-arrow-circle-o-right"></i>
          </a>
        ) : (
          ''
        )}
        {!state.oneOnOneWizardIsComplete &&
        state.eventHasPeerToPeer &&
        state.surveyUserRegistrationStatusKey ===
          'registration_status_approved' &&
        peerToPeerWizardIsAvailable ? (
            <a href={`${params.token}/profile/wizard`} className="header-alert">
              <strong>ALERT</strong> Complete Peer-to-Peer Setup{' '}
              <i className="fa fa-arrow-circle-o-right"></i>
            </a>
          ) : (
            ''
          )}
      </div>
      {!state.eventAgendaStatusKey ||
      state.eventAgendaStatusKey != 'event_agenda_status_full_agenda' ? (
          <NoAgenda
            eventMarketingUrl={state.eventMarketingUrl}
            userTitle={state.userTitle}
            userOrganizationName={state.userOrganizationName}
            userFullName={state.userFullName}
          />
        ) : (
          <div className="container">
            <div className="welcome">
              <h2>Welcome {state.userFullName}</h2>
              <h5>
                {state.userTitle}
                {state.userTitle !== null && state.userOrganizationName !== null
                  ? ', '
                  : ''}
                {state.userOrganizationName}
              </h5>
            </div>
            <div className="virtual-sessions-page">
              {/* <span onClick={ this.toggleFilterScreen } className='custom-filter'></span> */}
              <div className="schedule-filter">
                <button
                  onClick={() => toggleFilter('mySchedule')}
                  className="filter-tabs"
                >
                  <span
                    id="agenda"
                    className={'agenda ' + (!buttonStatus ? 'active' : '')}
                  >
                  AGENDA
                  </span>
                  <span
                    id="my-schedule"
                    className={'my-schedule ' + (buttonStatus ? 'active' : '')}
                  >
                  MY SCHEDULE
                  </span>
                </button>
              </div>
              <div className="session-advisory">
                <br />
                <p>
                Welcome to your Virtual Event Portal, your one-stop access to
                all virtual sessions. Please make sure to select the session(s)
                you would like to participate in via the &quot;add to my
                schedule&quot; button. This will secure your spot and enable you
                to join each session 5 minutes prior to the start time.
                </p>
                {renderSwitch()}
              </div>
              {getTimeBlocks()}
            </div>
          </div>
        )}
      <div className="container">
        {state.isEventOver && state.hasThankYou && (
          <div className="session-closing-details">
            <p>
              <strong>
                Thank you for participating in the {state.eventName}.
              </strong>
              <br />
              To provide feedback or to view upcoming programs, please{' '}
              <span
                className="pseudo-link"
                onClick={() =>
                  setState({ ...state, showEvalModal: true, isEventOver: true })
                }
              >
                click here
              </span>
              .
            </p>
          </div>
        )}
      </div>
      {state.showEvalModal && state.hasThankYou && (
        <EventEvalModal
          handleCloseEventEvalModal={handleCloseEventEvalModal}
          eventName={state.eventName}
          eventEvalUrl={state.eventEvalUrl}
          upcomingEvents={state.upcomingEvents}
        />
      )}
      {state.genericModalIsOpen && (
        <GenericPopUpModal
          handleCloseGenericModal={handleCloseGenericModal}
          content={state.genericModalContent}
          customCancelButton={state.genericModalCustomCancelButton}
        />
      )}
    </div>
  )
}

export default Sessions
